export interface CentralIaStreamEvent {
  type: 'start' | 'chunk' | 'done' | 'title' | 'error' | 'files_missing';
  chat?: any;
  user_message?: any;
  assistant_message?: any;
  content?: string;
  chat_id?: number;
  title?: string;
  message?: string;
  file_ids?: string[];
}

export interface CentralIaStreamBody {
  provider: string;
  model?: string;
  prompt: string;
  chat_id?: number | null;
  folder_id?: number | null;
  web_search?: boolean;
  files?: Array<{
    file_id: string | number;
    file_name?: string;
    content_type?: string;
    size_bytes?: number;
    s3_url?: string;
  }>;
}

export type CentralIaStreamOutcome = 'done' | 'error' | 'aborted';

const CONNECTION_ERROR =
  'Não foi possível conectar à Central IA. Verifique sua conexão e tente novamente.';
const DROPPED_ERROR = 'A conexão caiu antes do fim da resposta. Tente novamente.';
const SESSION_ERROR = 'Sua sessão expirou. Entre novamente na plataforma para continuar.';
const GENERIC_ERROR = 'Não foi possível obter resposta. Tente novamente.';

export const streamCentralIaMessage = async (
  body: CentralIaStreamBody,
  onEvent: (event: CentralIaStreamEvent) => void,
  signal?: AbortSignal,
): Promise<CentralIaStreamOutcome> => {
  let outcome = 'error' as CentralIaStreamOutcome;
  let errorReported = false;
  let connected = false;
  const reportError = (message: string) => {
    if (errorReported) return;
    errorReported = true;
    onEvent({ type: 'error', message });
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_ENDPOINT}/chatbots/central_ia/chat/stream`,
      {
        method: 'POST',
        signal,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          frontend: 'true',
        },
        body: JSON.stringify(body),
      },
    );
    connected = true;
    if (!response.ok || !response.body) {
      reportError([401, 423].includes(response.status) ? SESSION_ERROR : GENERIC_ERROR);
      return 'error';
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finished = false;

    const handleFrame = (frame: string) => {
      const line = frame.split('\n').find((l) => l.startsWith('data: '));
      if (!line) return;
      try {
        const event: CentralIaStreamEvent = JSON.parse(line.slice(6));
        if (event.type === 'done') outcome = 'done';
        if (event.type === 'error') {
          if (errorReported) return;
          errorReported = true;
        }
        onEvent(event);
      } catch {
        // frame malformado: ignora e segue o stream
      }
    };

    while (!finished) {
      // eslint-disable-next-line no-await-in-loop
      const chunk = await reader.read();
      finished = chunk.done;
      if (chunk.value) {
        buffer += decoder.decode(chunk.value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        frames.forEach(handleFrame);
      }
    }
    if (buffer.trim()) handleFrame(buffer);
    if (outcome !== 'done') reportError(DROPPED_ERROR);
    return outcome;
  } catch (err: any) {
    if (err?.name === 'AbortError') return 'aborted';
    if (outcome === 'done') return outcome;
    reportError(connected ? DROPPED_ERROR : CONNECTION_ERROR);
    return 'error';
  }
};
